import { Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';

import { AssistantApprovalCard } from '@core/models/assistant/assistant.model';
import { AssistantService } from '@core/services/assistant/assistant.service';
import { ICONS } from '@core/services/icons/icons.service';

const MAX_PILLS = 4;
const MAX_VALUE = 40;

const cut = (value: string) =>
  value.length > MAX_VALUE ? `${value.slice(0, MAX_VALUE - 1)}…` : value;

/** The arguments worth a glance; everything else is in "Show request". */
export function argumentPills(
  args: Record<string, unknown>
): { key: string; value: string }[] {
  const pills: { key: string; value: string }[] = [];
  for (const [key, value] of Object.entries(args)) {
    if (pills.length === MAX_PILLS) break;
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      pills.push({ key, value: cut(String(value)) });
    } else if (
      Array.isArray(value) &&
      value.length > 0 &&
      value.length <= 3 &&
      value.every((v) => typeof v === 'string' || typeof v === 'number')
    ) {
      pills.push({ key, value: cut(value.join(', ')) });
    }
  }
  return pills;
}

@Component({
  selector: 'app-assistant-approval-card',
  imports: [TranslatePipe],
  templateUrl: './assistant-approval-card.component.html',
  styles: ``
})
export class AssistantApprovalCardComponent {
  readonly card = input.required<AssistantApprovalCard>();
  readonly messageId = input.required<string>();
  protected readonly assistant = inject(AssistantService);
  private readonly translate = inject(TranslateService);
  protected readonly ICONS = ICONS;
  private readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(map((e) => e.lang)),
    { initialValue: this.translate.getCurrentLang() }
  );

  protected readonly destructive = computed(() => this.card().access === 'destructive');
  protected readonly pills = computed(() => argumentPills(this.card().arguments));
  protected readonly json = computed(() => JSON.stringify(this.card().arguments, null, 2));
  // A live-edit call names only its edit session; the card knows the plan that session holds.
  private readonly labelArgs = computed(() => {
    const { arguments: args, planId } = this.card();
    return planId ? { ...args, plan_id: planId } : args;
  });
  protected readonly title = computed(() => this.label('title'));
  protected readonly handoverTitle = computed(() => {
    this.currentLang();
    return this.translate.instant(
      'features.plan.edit.handover.title',
      this.card().arguments
    );
  });
  /** The hand-over under way: it waits on the edit session, so its buttons stay off meanwhile. */
  protected readonly handingOver = signal<'save' | 'discard' | null>(null);
  protected readonly confirmLabel = computed(() => this.label('confirm'));
  protected readonly target = computed(() => {
    this.currentLang();
    const { name } = this.card();
    const args = this.labelArgs();
    const own = `assistant.approvals.target.${name}`;
    const specific = this.translate.instant(own, args);
    if (specific !== own) return specific;
    return this.translate.instant(
      typeof args['plan_id'] === 'string'
        ? 'assistant.approvals.target.plan'
        : 'assistant.approvals.target.data_space',
      args
    );
  });

  protected answer(approved: boolean, alwaysAllow = false): void {
    this.assistant.answerApproval(
      this.messageId(),
      this.card().approvalId,
      approved,
      alwaysAllow
    );
  }

  protected async handOver(end: 'save' | 'discard'): Promise<void> {
    this.handingOver.set(end);
    try {
      await this.assistant.handOver(this.messageId(), this.card().approvalId, end);
    } finally {
      this.handingOver.set(null);
    }
  }

  private label(kind: 'title' | 'confirm'): string {
    this.currentLang();
    const { name } = this.card();
    const key = `assistant.approvals.${kind}.${name}`;
    const value = this.translate.instant(key, this.labelArgs());
    return value === key
      ? this.translate.instant(`assistant.approvals.${kind}.generic`, { name })
      : value;
  }
}
